import type { EmbedMode } from './UseHestiaIframe'

export const HESTIA_TO_PARENT_RELAY_REQUEST = 'HESTIA_TO_PARENT_RELAY_REQUEST'
export const HESTIA_INBOX_ROUTE_SYNC = 'HESTIA_INBOX_ROUTE_SYNC'
export const HESTIA_INBOX_UNREAD_SYNC = 'HESTIA_INBOX_UNREAD_SYNC'
export const HESTIA_PHONE_ORDERING_ROUTE_SYNC =
  'HESTIA_PHONE_ORDERING_ROUTE_SYNC'
export const HESTIA_EVENTS_NAVIGATE = 'HESTIA_EVENTS_NAVIGATE'
export const HESTIA_PRINT_SHOP_ROUTE_SYNC = 'HESTIA_PRINT_SHOP_ROUTE_SYNC'
export const HESTIA_ORDERS_ROUTE_SYNC = 'HESTIA_ORDERS_ROUTE_SYNC'
export const HESTIA_UPSELL_OPEN_REQUEST = 'HESTIA_UPSELL_OPEN_REQUEST'
export const PARENT_TO_HESTIA_EVENT = 'PARENT_TO_HESTIA_EVENT'
// Parent (Hermes) → iframe: drive the embedded inbox to a thread (or the list,
// when null) so dashboard back/forward navigates between threads. The inverse
// of HESTIA_INBOX_ROUTE_SYNC. MUST stay in sync with the mirror constant in
// apps/hestia/src/utilities/iframe/hestiaIframeEvents.ts (no shared import).
export const PARENT_INBOX_NAVIGATE = 'PARENT_INBOX_NAVIGATE'
// Print-shop history contract, child ↔ parent (mirror constants in
// apps/hestia/src/utilities/iframe/hestiaIframeEvents.ts — no shared import;
// change both or the receiver silently drops every message):
// - The parent owns ALL browser history while the print shop is embedded. The
//   child must never push its own history entry; it replaces locally and posts
//   HESTIA_PRINT_SHOP_ROUTE_SYNC carrying the *intended* navigation
//   ('push' | 'replace' | 'back') for the parent to perform, so every logical
//   screen is exactly one joint history entry.
// - PARENT_PRINT_SHOP_NAVIGATE is the inverse: parent history moved (browser
//   back/forward, deep link), drive the mounted iframe to `view`/`quoteId`/
//   `orderId`. The child applies it with replace only.
// - All fields beyond `type` are optional-or-nullable on both messages so old
//   and new deployments of either app interoperate during rollout.
export const PARENT_PRINT_SHOP_NAVIGATE = 'PARENT_PRINT_SHOP_NAVIGATE'
export const HESTIA_WEBSITE_BUILDER_LAYOUT_READY =
  'HESTIA_WEBSITE_BUILDER_LAYOUT_READY'
export const HESTIA_WEBSITE_AGENT_SIDEBAR_TAB_SYNC =
  'HESTIA_WEBSITE_AGENT_SIDEBAR_TAB_SYNC'
// Website-builder draft navigation protocol. These constants mirror
// apps/hestia/src/utilities/iframe/websiteBuilderDraftRelay.ts.
export const HESTIA_WEBSITE_DRAFT_STATUS = 'HESTIA_WEBSITE_DRAFT_STATUS'
export const PARENT_WEBSITE_LEAVE_REQUEST = 'PARENT_WEBSITE_LEAVE_REQUEST'
export const PARENT_WEBSITE_LEAVE_CANCEL = 'PARENT_WEBSITE_LEAVE_CANCEL'
export const HESTIA_WEBSITE_LEAVE_ACTION_STARTED =
  'HESTIA_WEBSITE_LEAVE_ACTION_STARTED'
export const HESTIA_WEBSITE_LEAVE_RESULT = 'HESTIA_WEBSITE_LEAVE_RESULT'
// Embedded Hestia asks Hermes to run the GBP Google OAuth at top level — Google
// rejects OAuth driven from inside the iframe. MUST stay in sync with the mirror
// constant in apps/hestia/src/utilities/iframe/gbpOAuthRelay.ts — together they
// form the postMessage protocol across the app boundary (no shared import).
// Change both or this receiver silently drops every message.
export const HESTIA_GBP_OAUTH_REQUEST = 'HESTIA_GBP_OAUTH_REQUEST'
// Embedded Hestia's GBP location picker asks Hermes to re-scope the embed to
// the picked location — an in-frame cross-location navigation gets yanked back
// to the location the embed is pinned to. MUST stay in sync with the mirror
// constant in apps/hestia/src/utilities/iframe/launchLocationPickRelay.ts —
// together they form the postMessage protocol across the app boundary (no
// shared import). Change both or this receiver silently drops every message.
export const HESTIA_LAUNCH_LOCATION_PICK = 'HESTIA_LAUNCH_LOCATION_PICK'
// Embedded Hestia asks Hermes to raise/lower a full-window scrim while a
// blocking modal is open inside the iframe, so the Hermes left-nav dims along
// with the embed content (the in-iframe overlay can't reach outside the embed
// box). MUST stay in sync with the mirror constant in
// apps/hestia/src/utilities/iframe/modalScrimRelay.ts — together they form the
// postMessage protocol across the app boundary (no shared import). Change both
// or this receiver silently drops every message.
export const HESTIA_MODAL_SCRIM_SYNC = 'HESTIA_MODAL_SCRIM_SYNC'

export enum HestiaIframeTarget {
  Loyalty = 'loyalty',
  OwnerAgentWidget = 'owner-agent-widget',
  WebsiteBuilder = 'website-builder-hermes',
}

export enum HestiaIframeEvent {
  MenuDataInvalidate = 'menu-data.invalidate',
  WebsitePreviewReload = 'website-preview.reload',
}

export const HESTIA_ORDERS_ROUTE_QUERY_KEYS = [
  'end',
  'menuType',
  'orderId',
  'orderLocationId',
  'page',
  'query',
  'start',
  'status',
  'tab',
  'taxExemptionEnabled',
  'type',
] as const

export type HestiaOrdersRouteQueryKey =
  (typeof HESTIA_ORDERS_ROUTE_QUERY_KEYS)[number]

export type HestiaOrdersRouteQuery = Record<
  HestiaOrdersRouteQueryKey,
  string | undefined
>

export interface HestiaToParentRelayRequest {
  event: HestiaIframeEvent
  target: HestiaIframeTarget
  type: typeof HESTIA_TO_PARENT_RELAY_REQUEST
}

export interface HestiaWebsiteBuilderLayoutReadyMessage {
  type: typeof HESTIA_WEBSITE_BUILDER_LAYOUT_READY
}

export type HestiaWebsiteAgentSidebarTab = 'agent' | 'editor'

export interface HestiaWebsiteAgentSidebarTabSyncMessage {
  tab: HestiaWebsiteAgentSidebarTab
  type: typeof HESTIA_WEBSITE_AGENT_SIDEBAR_TAB_SYNC
}

export interface HestiaWebsiteDraftStatusMessage {
  hasUnpublishedChanges: boolean
  type: typeof HESTIA_WEBSITE_DRAFT_STATUS
}

export interface ParentWebsiteLeaveRequestMessage {
  requestId: string
  type: typeof PARENT_WEBSITE_LEAVE_REQUEST
}

export interface ParentWebsiteLeaveCancelMessage {
  requestId: string
  type: typeof PARENT_WEBSITE_LEAVE_CANCEL
}

export interface HestiaWebsiteLeaveResultMessage {
  requestId: string
  shouldLeave: boolean
  type: typeof HESTIA_WEBSITE_LEAVE_RESULT
}

export interface HestiaWebsiteLeaveActionStartedMessage {
  requestId: string
  type: typeof HESTIA_WEBSITE_LEAVE_ACTION_STARTED
}

export interface HestiaPhoneOrderingRouteSyncMessage {
  callId: string | null
  type: typeof HESTIA_PHONE_ORDERING_ROUTE_SYNC
}

export interface HestiaEventsNavigateMessage {
  type: typeof HESTIA_EVENTS_NAVIGATE
}

export interface HestiaPrintShopRouteSyncMessage {
  checkoutQuoteId?: string | null
  navigation?: PrintShopNavigation | null
  orderId: string | null
  quoteId?: string | null
  requiresCheckoutHistoryAck?: boolean
  type: typeof HESTIA_PRINT_SHOP_ROUTE_SYNC
  view?: PrintShopView | null
}

const PRINT_SHOP_NAVIGATIONS = ['back', 'push', 'replace'] as const
export type PrintShopNavigation = (typeof PRINT_SHOP_NAVIGATIONS)[number]

const PRINT_SHOP_VIEWS = [
  'landing',
  'goal',
  'design',
  'order',
  'quote',
  'checkout',
] as const
export type PrintShopView = (typeof PRINT_SHOP_VIEWS)[number]

export interface HestiaOrdersRouteSyncMessage {
  // The order's own location — carried alongside orderId so a deep link can
  // reopen a brand-scoped order that lives at a different location than the
  // dashboard's active one (getOrder is location-scoped).
  locationId: string | null
  orderId: string | null
  query?: Partial<HestiaOrdersRouteQuery>
  type: typeof HESTIA_ORDERS_ROUTE_SYNC
}

export interface HestiaInboxRouteSyncMessage {
  threadId: string | null
  type: typeof HESTIA_INBOX_ROUTE_SYNC
}

export interface HestiaInboxUnreadSyncMessage {
  type: typeof HESTIA_INBOX_UNREAD_SYNC
}

export interface ParentToHestiaEvent {
  event: HestiaIframeEvent
  source?: HestiaIframeTarget
  type: typeof PARENT_TO_HESTIA_EVENT
}

export interface ParentInboxNavigateMessage {
  threadId: string | null
  type: typeof PARENT_INBOX_NAVIGATE
}

export interface ParentPrintShopNavigateMessage {
  orderId: string | null
  quoteId: string | null
  type: typeof PARENT_PRINT_SHOP_NAVIGATE
  view: PrintShopView
}

export type ParentPrintShopNavigateTarget = Omit<
  ParentPrintShopNavigateMessage,
  'type'
>

export interface HestiaGbpOAuthRequestMessage {
  brandId: string
  locationId: string
  type: typeof HESTIA_GBP_OAUTH_REQUEST
}

export interface HestiaLaunchLocationPickMessage {
  brandId: string
  locationId: string
  type: typeof HESTIA_LAUNCH_LOCATION_PICK
}

export interface HestiaModalScrimSyncMessage {
  isOpen: boolean
  type: typeof HESTIA_MODAL_SCRIM_SYNC
}

export interface HestiaUpsellOpenRequestMessage {
  source: string
  type: typeof HESTIA_UPSELL_OPEN_REQUEST
}

interface BuildParentToHestiaEventParams {
  event: HestiaIframeEvent
  source?: HestiaIframeTarget
}

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return Boolean(value) && typeof value === 'object'
}

const isString = (value: unknown): value is string => typeof value === 'string'

const isNullishOr = <T>(
  value: unknown,
  isValid: (candidate: unknown) => candidate is T
): value is T | null | undefined =>
  value === undefined || value === null || isValid(value)

const isHestiaOrdersRouteQuery = (
  value: unknown
): value is Partial<HestiaOrdersRouteQuery> => {
  if (!isRecord(value)) return false

  return Object.entries(value).every(([key, queryValue]) => {
    return (
      HESTIA_ORDERS_ROUTE_QUERY_KEYS.includes(
        key as HestiaOrdersRouteQueryKey
      ) &&
      (typeof queryValue === 'string' || queryValue === undefined)
    )
  })
}

export const isHestiaGbpOAuthRequest = (
  data: unknown
): data is HestiaGbpOAuthRequestMessage => {
  return (
    isRecord(data) &&
    data.type === HESTIA_GBP_OAUTH_REQUEST &&
    typeof data.brandId === 'string' &&
    typeof data.locationId === 'string'
  )
}

export const isHestiaLaunchLocationPick = (
  data: unknown
): data is HestiaLaunchLocationPickMessage => {
  return (
    isRecord(data) &&
    data.type === HESTIA_LAUNCH_LOCATION_PICK &&
    typeof data.brandId === 'string' &&
    typeof data.locationId === 'string'
  )
}

export const isHestiaModalScrimSyncMessage = (
  value: unknown
): value is HestiaModalScrimSyncMessage => {
  return (
    isRecord(value) &&
    value.type === HESTIA_MODAL_SCRIM_SYNC &&
    typeof value.isOpen === 'boolean'
  )
}

export const isHestiaUpsellOpenRequestMessage = (
  value: unknown
): value is HestiaUpsellOpenRequestMessage => {
  return (
    isRecord(value) &&
    value.type === HESTIA_UPSELL_OPEN_REQUEST &&
    typeof value.source === 'string'
  )
}

export const isHestiaIframeTarget = (
  value: unknown
): value is HestiaIframeTarget => {
  return Object.values(HestiaIframeTarget).includes(value as HestiaIframeTarget)
}

export const isHestiaIframeEvent = (
  value: unknown
): value is HestiaIframeEvent => {
  return Object.values(HestiaIframeEvent).includes(value as HestiaIframeEvent)
}

export const normalizeEmbedToHestiaIframeTarget = (
  embed: EmbedMode | null | undefined
): HestiaIframeTarget | null => {
  switch (embed) {
    case 'loyalty':
      return HestiaIframeTarget.Loyalty
    case 'owner-agent-widget':
      return HestiaIframeTarget.OwnerAgentWidget
    case 'website-builder-hermes':
      return HestiaIframeTarget.WebsiteBuilder
    default:
      return null
  }
}

export const buildParentToHestiaEvent = ({
  event,
  source,
}: BuildParentToHestiaEventParams): ParentToHestiaEvent => {
  return {
    event,
    type: PARENT_TO_HESTIA_EVENT,
    ...(source !== undefined ? { source } : {}),
  }
}

export const buildParentInboxNavigateMessage = (
  threadId: string | null
): ParentInboxNavigateMessage => {
  return {
    threadId,
    type: PARENT_INBOX_NAVIGATE,
  }
}

export const buildParentPrintShopNavigateMessage = (
  target: ParentPrintShopNavigateTarget
): ParentPrintShopNavigateMessage => ({
  ...target,
  type: PARENT_PRINT_SHOP_NAVIGATE,
})

export const isPrintShopView = (value: unknown): value is PrintShopView => {
  return PRINT_SHOP_VIEWS.includes(value as PrintShopView)
}

const isPrintShopNavigation = (
  value: unknown
): value is PrintShopNavigation => {
  return PRINT_SHOP_NAVIGATIONS.includes(value as PrintShopNavigation)
}

export const isHestiaToParentRelayRequest = (
  value: unknown
): value is HestiaToParentRelayRequest => {
  if (!isRecord(value)) return false

  return (
    value.type === HESTIA_TO_PARENT_RELAY_REQUEST &&
    isHestiaIframeTarget(value.target) &&
    isHestiaIframeEvent(value.event)
  )
}

export const isHestiaWebsiteBuilderLayoutReadyMessage = (
  value: unknown
): value is HestiaWebsiteBuilderLayoutReadyMessage => {
  return isRecord(value) && value.type === HESTIA_WEBSITE_BUILDER_LAYOUT_READY
}

export const isHestiaWebsiteAgentSidebarTabSyncMessage = (
  value: unknown
): value is HestiaWebsiteAgentSidebarTabSyncMessage => {
  return (
    isRecord(value) &&
    value.type === HESTIA_WEBSITE_AGENT_SIDEBAR_TAB_SYNC &&
    (value.tab === 'agent' || value.tab === 'editor')
  )
}

export const isHestiaEventsNavigateMessage = (
  value: unknown
): value is HestiaEventsNavigateMessage => {
  return isRecord(value) && value.type === HESTIA_EVENTS_NAVIGATE
}

export const isHestiaWebsiteDraftStatusMessage = (
  value: unknown
): value is HestiaWebsiteDraftStatusMessage => {
  return (
    isRecord(value) &&
    value.type === HESTIA_WEBSITE_DRAFT_STATUS &&
    typeof value.hasUnpublishedChanges === 'boolean'
  )
}

export const isHestiaWebsiteLeaveResultMessage = (
  value: unknown
): value is HestiaWebsiteLeaveResultMessage => {
  return (
    isRecord(value) &&
    value.type === HESTIA_WEBSITE_LEAVE_RESULT &&
    typeof value.requestId === 'string' &&
    typeof value.shouldLeave === 'boolean'
  )
}

export const isHestiaWebsiteLeaveActionStartedMessage = (
  value: unknown
): value is HestiaWebsiteLeaveActionStartedMessage => {
  return (
    isRecord(value) &&
    value.type === HESTIA_WEBSITE_LEAVE_ACTION_STARTED &&
    typeof value.requestId === 'string'
  )
}

export const isParentWebsiteLeaveCancelMessage = (
  value: unknown
): value is ParentWebsiteLeaveCancelMessage => {
  return (
    isRecord(value) &&
    value.type === PARENT_WEBSITE_LEAVE_CANCEL &&
    typeof value.requestId === 'string'
  )
}

export const buildParentWebsiteLeaveRequestMessage = (
  requestId: string
): ParentWebsiteLeaveRequestMessage => ({
  requestId,
  type: PARENT_WEBSITE_LEAVE_REQUEST,
})

export const buildParentWebsiteLeaveCancelMessage = (
  requestId: string
): ParentWebsiteLeaveCancelMessage => ({
  requestId,
  type: PARENT_WEBSITE_LEAVE_CANCEL,
})

export const isHestiaPhoneOrderingRouteSyncMessage = (
  value: unknown
): value is HestiaPhoneOrderingRouteSyncMessage => {
  if (!isRecord(value)) return false

  return (
    value.type === HESTIA_PHONE_ORDERING_ROUTE_SYNC &&
    (typeof value.callId === 'string' || value.callId === null)
  )
}

export const isHestiaPrintShopRouteSyncMessage = (
  value: unknown
): value is HestiaPrintShopRouteSyncMessage => {
  if (!isRecord(value)) return false

  return (
    value.type === HESTIA_PRINT_SHOP_ROUTE_SYNC &&
    isNullishOr(value.navigation, isPrintShopNavigation) &&
    isNullishOr(value.view, isPrintShopView) &&
    (isString(value.orderId) || value.orderId === null) &&
    isNullishOr(value.checkoutQuoteId, isString) &&
    isNullishOr(value.quoteId, isString) &&
    (value.requiresCheckoutHistoryAck === undefined ||
      typeof value.requiresCheckoutHistoryAck === 'boolean')
  )
}

export const isHestiaOrdersRouteSyncMessage = (
  value: unknown
): value is HestiaOrdersRouteSyncMessage => {
  if (!isRecord(value)) return false

  return (
    value.type === HESTIA_ORDERS_ROUTE_SYNC &&
    (typeof value.orderId === 'string' || value.orderId === null) &&
    (typeof value.locationId === 'string' || value.locationId === null) &&
    (value.query === undefined || isHestiaOrdersRouteQuery(value.query))
  )
}

export const isHestiaInboxRouteSyncMessage = (
  value: unknown
): value is HestiaInboxRouteSyncMessage => {
  if (!isRecord(value)) return false

  return (
    value.type === HESTIA_INBOX_ROUTE_SYNC &&
    (typeof value.threadId === 'string' || value.threadId === null)
  )
}

export const isHestiaInboxUnreadSyncMessage = (
  value: unknown
): value is HestiaInboxUnreadSyncMessage => {
  if (!isRecord(value)) return false

  return value.type === HESTIA_INBOX_UNREAD_SYNC
}
